import { useState, useEffect } from 'react';
import type { Mercury } from '@repo/types/Models';
import { getVisibleAnnouncements, dismissAnnouncement } from '@repo/util/announcementStorage';
import { LexicalRenderer } from '@repo/ui/LexicalEditor';
import { useUser } from '@repo/hooks/useUser';

const AnnouncementBanner = () => {
  const user = useUser();
  const [announcement, setAnnouncement] = useState<Mercury.Models.Announcement | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const visibleAnnouncements = getVisibleAnnouncements(user.id);
    // Show the most recent one
    if (visibleAnnouncements.length > 0) {
      setAnnouncement(visibleAnnouncements[0]);
    }
  }, [user?.id]);

  const handleDismiss = () => {
    if (announcement && user?.id) {
      dismissAnnouncement(user.id, announcement.id);
      setAnnouncement(null);
    }
  };

  if (!announcement) {
    return null;
  }

  const getBackgroundClass = () => {
    switch (announcement.type) {
      case 'info':
        return 'bg-blue-50 border-blue-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-grey-50 border-grey-200';
    }
  };

  const getIconColorClass = () => {
    switch (announcement.type) {
      case 'info':
        return 'text-blue-600';
      case 'warning':
        return 'text-yellow-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-grey-600';
    }
  };

  const getIcon = () => {
    switch (announcement.type) {
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'success':
        return 'check_circle';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <div className={`w-full border-b ${getBackgroundClass()}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-start gap-3">
          <span className={`material-symbols-outlined flex-shrink-0 ${getIconColorClass()}`}>
            {getIcon()}
          </span>
          <div className="flex-1 min-w-0">
            {announcement.title && (
              <h4 className="font-semibold text-grey-900 mb-1">{announcement.title}</h4>
            )}
            <div className="text-sm text-grey-700">
              <LexicalRenderer content={announcement.content} />
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 text-grey-500 hover:text-grey-700 transition-colors"
            aria-label="Dismiss announcement"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
